import React, { useMemo } from 'react';
import { AppIcon } from '../AppIcon';
import { iconService } from '../../services/iconService';
import { HEALTH_APP_ICONS } from './healthAppIcons';
import './AppSquircle.scss';

export type AppSquircleSize = 'sm' | 'md' | 'lg';

interface AppSquircleProps {
  appId: string;
  size?: AppSquircleSize;
  className?: string;
}

// Icon pixel size inside the squircle for each size variant
const ICON_PX: Record<AppSquircleSize, number> = {
  sm: 12,
  md: 20,
  lg: 26,
};

/**
 * Renders an app icon inside a colored squircle (superellipse) background.
 * Used in list items, sidebar dropdowns, and app launchers.
 *
 * Uses AppIcon (the battle-tested renderer) with explicit pixel dimensions so
 * there is no dependency on dynamic Phosphor namespace lookups.
 */
export const AppSquircle: React.FC<AppSquircleProps> = ({ appId, size = 'sm', className = '' }) => {
  const px = ICON_PX[size];

  // getPhosphorAppIcon reads only the static APP_PHOSPHOR_ICONS map — it always
  // returns a symbolic (Phosphor) icon regardless of any image icon stored in the
  // app registry / backend (e.g. /icons/drive.png). This is intentional: squircles
  // need a scalable icon that can be tinted white, not a raster image.
  //
  // Apps in HEALTH_APP_ICONS opt into a healthicons-react glyph so the squircle
  // here matches the same icon used inside the app's own header.
  const iconConfig = useMemo(() => {
    const health = HEALTH_APP_ICONS[appId];
    if (health) {
      return {
        type: 'component' as const,
        component: health.component,
        color: health.color,
        metadata: { source: 'app' as const, appId, isAppIcon: false },
      };
    }
    return iconService.getPhosphorAppIcon(appId) ?? {
      type: 'phosphor' as const,
      phosphorIcon: 'AppWindow',
      phosphorWeight: 'duotone' as const,
      color: '#6366f1',
      metadata: { source: 'default' as const, isAppIcon: false },
    };
  }, [appId]);

  const appColor = (iconConfig as any).color || '#6366f1';

  // Strip color so the icon inherits white from the squircle CSS instead of rendering
  // in the brand color (which would be invisible against the tinted background).
  const iconForSquircle = useMemo(() => ({
    ...iconConfig,
    color: undefined,
    componentProps: iconConfig.type === 'component' ? { width: px, height: px } : undefined,
    metadata: { ...iconConfig.metadata, isAppIcon: false },
  }), [iconConfig, px]);

  return (
    <span
      className={`app-squircle app-squircle--${size} ${className}`}
      style={{ '--app-color': appColor } as React.CSSProperties}
    >
      <AppIcon icon={iconForSquircle} width={px} height={px} />
    </span>
  );
};

export default AppSquircle;
